import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {sendEvent} from '@github-ui/hydro-analytics'
import {testIdProps} from '@github-ui/test-id-props'
import {CheckIcon} from '@primer/octicons-react'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import {useChatMessageBehavior} from '../hooks/use-chat-message-behavior'
import {
  filterUniqueConfirmations,
  getMcpToolResults,
  getRenderableReferences,
  isAgent,
} from '../utils/copilot-chat-helpers'
import type {CopilotChatReference, Invocation, SnippetReference} from '../utils/copilot-chat-types'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {ChatImage} from './ChatImage'
import styles from './ChatMessage.module.css'
import {type ChatMessageContextProps, ChatMessageProvider, useChatMessage} from './ChatMessageContext'
import {ChatMessageMcpToolResultsList} from './ChatMessageMcpToolResultsList'
import {ChatMessageReferencesList, ChatMessageReferenceTokens} from './ChatReferences'
import {Confirmation} from './Confirmation'
import {CopilotBadge} from './CopilotBadgeV2'
import {AgentErrors, ErrorMessage} from './Errors'
import {Feedback} from './Feedback'
import {messageFromFunctionCall} from './FunctionLoadingUtils'
import {InterruptedBanner} from './InterruptedBanner'
import {RetryButton} from './RetryButton'
import {UserMessage} from './UserMessage'
import {WithShimmerEffect} from './WithShimmerEffect'

export interface ChatMessageProps extends ChatMessageContextProps, InnerChatMessageProps {}

type InnerChatMessageProps = {
  isLatestMessage?: boolean
  isFirstReply?: boolean
  inputRef?: React.RefObject<HTMLInputElement> | React.RefObject<HTMLTextAreaElement>
  isStreaming?: boolean
  excludeFeedback?: boolean
  onClickReference?: (reference: CopilotChatReference, event?: React.MouseEvent<HTMLAnchorElement>) => void
}

const InnerChatMessage = ({
  isLatestMessage,
  isFirstReply,
  isStreaming,
  onClickReference,
  ...props
}: InnerChatMessageProps) => {
  const {message} = useChatMessage()
  const state = useChatState()
  const manager = useChatManager()

  const {
    author,
    handleConfirmationAction,
    handleRetryErrorMessage,
    hasClientConfirmations,
    isAI,
    isCopilot,
    isError,
    isAgentError,
    isInterrupted,
    isUser: isFromUser,
    myRef,
    rendererConfig,
    renderFeedback,
    shouldShowMessageActions,
    skillExecutionToUseForRespondingText,
    showRetryButton,
  } = useChatMessageBehavior({
    excludeFeedback: props.excludeFeedback,
    inputRef: props.inputRef,
    isLatestMessage,
    isStreaming,
    excludeReferencesFromFocusZone: true,
  })
  const hasClientToolResults = message.clientToolResults && message.clientToolResults.length > 0
  const hasClientSkillsRequests = message.clientSkillsRequests && message.clientSkillsRequests.length > 0
  const isUser = isFromUser && !hasClientToolResults
  const isCopilotLoading = isStreaming || hasClientToolResults || hasClientSkillsRequests
  const isLoading = isCopilotLoading && isLatestMessage
  const isCopilotMessage = isCopilot || hasClientToolResults
  const renderableReferences = useMemo(() => getRenderableReferences(message.references ?? []), [message.references])
  const mcpToolResults = useMemo(() => getMcpToolResults(message.references ?? []), [message.references])
  const maxMessagesReached = useMemo(() => manager.maxMessagesReached(), [manager])

  // We don't render an image reference token since it renders separately as the image preview
  const tokenRenderableReferences = renderableReferences.filter(
    r => r.type !== 'image' && r.type !== 'org-instructions',
  )

  const images = useMemo(
    () => message.mediaContent?.filter(media => media.mediaType.startsWith('image/')),
    [message.mediaContent],
  )

  const shouldShowImages = useMemo(() => (typeof images !== 'undefined' && images.length > 0) || false, [images])

  const onLinkClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLAnchorElement
      const href = target.href
      const reference = renderableReferences.find(r => (r as SnippetReference).url === href)
      if (reference && state.mode === 'immersive') {
        manager.selectReference(reference)
        event.preventDefault()
      }
    },
    [manager, renderableReferences, state.mode],
  )

  const onWrapCodeLinesChange = useCallback(
    (wrap: boolean) => {
      manager.setWrapCodeLines(wrap)
    },
    [manager],
  )

  return (
    <div
      className={clsx('message-container', styles.messageContainer, isUser && styles.user, isAI && styles.ai)}
      ref={myRef}
      {...testIdProps(`message-${isError ? 'error' : isStreaming ? 'streaming' : message.id}`)}
    >
      {isUser && shouldShowImages && (
        <div>
          {images?.map(image => (
            <ChatImage src={image?.url || ''} key={image?.url || ''} alt="Uploaded image" mode={state.mode} />
          ))}
        </div>
      )}
      {isCopilotMessage || hasClientToolResults ? (
        <CopilotBadge
          isLoading={isCopilotLoading && isLatestMessage}
          isError={isError && message.error?.type !== 'agentUnauthorized'}
          mode={state.mode}
          isFirstMessage={isFirstReply}
          isLoadingSkills={
            (message.skillExecutions ?? []).length > 0 && isCopilotLoading && message.content === '' && isLatestMessage
          }
          message={message.content || ''}
          createdAt={message.createdAt}
        />
      ) : isAI ? (
        <div className={styles.avatar}>
          <GitHubAvatar
            src={author.avatarURL}
            size={24}
            aria-label={`${author.name} avatar`}
            alt={`${author.name} avatar`}
            data-testid="chat-message-author-avatar"
          />
        </div>
      ) : null}
      {isUser && (
        <ChatMessageReferenceTokens
          size="small"
          className="mb-2"
          references={tokenRenderableReferences}
          message={message}
          onClickReference={onClickReference}
        />
      )}
      <div className={clsx(styles.message, isUser && styles.user)}>
        {hasClientConfirmations && message.clientConfirmations?.length ? (
          <Box
            className={clsx('my-1', styles.clientConfirmationContainer)}
            sx={{
              justifyContent: isUser ? 'flex-end' : '',
            }}
            data-testid="chat-message-client-confirmations"
          >
            <span className={styles.confirmationStatusText}>
              <CheckIcon /> {message.clientConfirmations?.[0]?.userLogin || 'User'}{' '}
              {message.clientConfirmations?.[0]?.state} the action
            </span>
          </Box>
        ) : null}
        {isCopilot && isLoading && !message.content && (
          <WithShimmerEffect className={clsx(styles.skillExecutionText, 'mb-2 mt-1 ml-2')}>
            {`${messageFromFunctionCall(skillExecutionToUseForRespondingText)}...`}
          </WithShimmerEffect>
        )}
        <div className={clsx(styles.messageContent, !!hasClientConfirmations && styles.hasClientConfirmations)}>
          {
            <>
              <div className={clsx('js-snippet-clipboard-copy-unpositioned', styles.messageContentWrapper)}>
                {message.role === 'assistant' &&
                  message.content &&
                  (renderableReferences?.length ? (
                    <ChatMessageReferencesList className="mb-2" references={renderableReferences} />
                  ) : null)}

                {message.role === 'assistant' &&
                  (mcpToolResults.length ? <ChatMessageMcpToolResultsList mcpToolResults={mcpToolResults} /> : null)}

                {!hasClientConfirmations &&
                  (isUser ? (
                    <UserMessage />
                  ) : (
                    message.content && (
                      <MarkdownRenderer
                        markdown={message.content}
                        onLinkClick={onLinkClick}
                        chatMode="assistive"
                        wrapCodeLines={state.wrapCodeLines}
                        onWrapCodeLinesChange={onWrapCodeLinesChange}
                        {...rendererConfig}
                      />
                    )
                  ))}
                {isError ? <ErrorMessage manager={manager} /> : null}
                {isAgentError && message.agentErrors?.length ? <AgentErrors errors={message.agentErrors} /> : null}
                {isInterrupted ? (
                  <InterruptedBanner messageHasContent={!!message.content || !!message.skillExecutions?.length} />
                ) : null}
                {(isCopilot || isAgent(author)) &&
                  filterUniqueConfirmations(message.confirmations).map((confirmation, i) => {
                    return (confirmation.confirmation as Invocation).name === 'indexrepo' ? null : (
                      <Confirmation
                        confirmation={confirmation}
                        handleConfirmation={handleConfirmationAction}
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={i}
                        isLatestMessage={isLatestMessage}
                      />
                    )
                  })}
              </div>
              {!shouldShowMessageActions && showRetryButton && (
                <RetryButton handleRetryMessage={handleRetryErrorMessage} disabled={maxMessagesReached} />
              )}
              {shouldShowMessageActions && (
                <Box
                  className={clsx('message-actions', styles.messageActionsContainer)}
                  data-testid="message-action-bar"
                  sx={{
                    // Floating actions for previous messages
                    position: isLatestMessage ? undefined : 'absolute',

                    bottom: isLatestMessage ? undefined : '-2.25rem',
                    borderRadius: isLatestMessage ? undefined : 2,
                    boxShadow: isLatestMessage ? undefined : 'var(--shadow-floating-small)',

                    // Opacity and pointer events rules instead of changing `display` let us animate the actions in and out
                    opacity: isLatestMessage ? undefined : 0,

                    pointerEvents: isLatestMessage ? undefined : 'none',

                    // Adds a lil invisible target to make hovering less precise so they don’t disappear if you miss a bit
                    '&::before': {
                      // Not on the inline actions though
                      content: isLatestMessage ? undefined : '""',

                      position: 'absolute',
                    },
                  }}
                >
                  {renderFeedback && <Feedback iconSize="small" />}
                  {isCopilot && !!message.content && !isStreaming && (
                    <CopyToClipboardButton
                      textToCopy={message.content ?? ''}
                      ariaLabel="Copy to clipboard"
                      size="small"
                      className="d-flex flex-items-center"
                      onCopy={() => {
                        sendEvent('copilot_chat.activate', {
                          target: 'CHAT_MESSAGE_COPY',
                          mode: state.mode,
                        })
                      }}
                    />
                  )}
                  {showRetryButton && (
                    <RetryButton handleRetryMessage={handleRetryErrorMessage} disabled={maxMessagesReached} />
                  )}
                </Box>
              )}
            </>
          }
        </div>
      </div>
    </div>
  )
}

export const ChatMessage = ({message, ...props}: ChatMessageProps) => (
  <ChatMessageProvider message={message}>
    <InnerChatMessage {...props} />
  </ChatMessageProvider>
)

try{ InnerChatMessage.displayName ||= 'InnerChatMessage' } catch {}
try{ ChatMessage.displayName ||= 'ChatMessage' } catch {}